import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Download, Mail, AlertCircle } from "lucide-react";

export default function TaxDocumentCenter() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [generating, setGenerating] = useState<string | null>(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: organization } = useQuery({
    queryKey: ["user-organization", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("organization_members")
        .select("organization_id, organizations(*)")
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: athleteTaxData, isLoading } = useQuery({
    queryKey: ["athlete-tax-summary", organization?.organization_id, selectedYear],
    queryFn: async () => {
      if (!organization?.organization_id) return [];
      
      const yearStart = `${selectedYear}-01-01`;
      const yearEnd = `${selectedYear}-12-31`;
      
      // Get all deals for this organization in the selected year
      const { data: deals, error: dealsError } = await supabase
        .from("deals")
        .select("athlete_id, amount, athletes(name, email, sport)")
        .eq("organization_id", organization.organization_id)
        .eq("status", "active")
        .gte("start_date", yearStart)
        .lte("start_date", yearEnd);
      
      if (dealsError) throw dealsError;

      // Group by athlete and calculate totals
      const athleteMap = new Map();
      
      deals?.forEach((deal) => {
        const athleteId = deal.athlete_id;
        if (!athleteMap.has(athleteId)) {
          athleteMap.set(athleteId, {
            athlete_id: athleteId,
            athlete_name: (deal.athletes as any)?.name || "Unknown",
            athlete_email: (deal.athletes as any)?.email,
            athlete_sport: (deal.athletes as any)?.sport,
            total_compensation: 0,
            deal_count: 0,
          });
        }
        
        const athlete = athleteMap.get(athleteId);
        athlete.total_compensation += Number(deal.amount);
        athlete.deal_count += 1;
      });

      return Array.from(athleteMap.values())
        .filter(a => a.total_compensation >= 600) // Only show athletes above threshold
        .sort((a, b) => b.total_compensation - a.total_compensation);
    },
    enabled: !!organization?.organization_id,
  });

  const generate1099 = async (athleteId: string) => {
    setGenerating(athleteId);
    try {
      const { data, error } = await supabase.functions.invoke("generate-1099", {
        body: { athleteId, taxYear: parseInt(selectedYear) },
      });

      if (error) throw error;

      toast.success("1099 generated successfully");
      // Could download PDF or show success
    } catch (error) {
      console.error("Error generating 1099:", error);
      toast.error("Failed to generate 1099");
    } finally {
      setGenerating(null);
    }
  };

  const generateAllForms = async () => {
    if (!athleteTaxData || athleteTaxData.length === 0) {
      toast.error("No athletes eligible for 1099 generation");
      return;
    }

    setBulkGenerating(true);
    let successCount = 0;
    let errorCount = 0;

    for (const athlete of athleteTaxData) {
      try {
        await supabase.functions.invoke("generate-1099", {
          body: { athleteId: athlete.athlete_id, taxYear: parseInt(selectedYear) },
        });
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setBulkGenerating(false);
    toast.success(`Generated ${successCount} forms${errorCount > 0 ? `, ${errorCount} errors` : ""}`);
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Tax Document Center</h1>
            <p className="text-muted-foreground">
              Generate and manage 1099-NEC forms for your athletes
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={generateAllForms}
              disabled={bulkGenerating || !athleteTaxData || athleteTaxData.length === 0}
            >
              <FileText className="h-4 w-4 mr-2" />
              {bulkGenerating ? "Generating..." : "Generate All 1099s"}
            </Button>
          </div>
        </div>

        {/* Tax Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Athletes Above Threshold</CardTitle>
              <CardDescription>Earned $600+ in {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{athleteTaxData?.length || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Total Compensation</CardTitle>
              <CardDescription>Sum of all deals in {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                ${athleteTaxData?.reduce((sum, a) => sum + a.total_compensation, 0).toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>IRS Deadline</CardTitle>
              <CardDescription>Form submission due date</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">Jan 31, {parseInt(selectedYear) + 1}</p>
            </CardContent>
          </Card>
        </div>

        {/* Athlete Tax Summary Table */}
        <Card>
          <CardHeader>
            <CardTitle>Athlete Tax Summary - {selectedYear}</CardTitle>
            <CardDescription>
              Athletes who earned $600 or more require 1099-NEC filing
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div>Loading tax data...</div>
            ) : athleteTaxData?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No athletes above threshold</h3>
                <p className="text-muted-foreground">
                  No athletes earned $600+ in {selectedYear}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete Name</TableHead>
                    <TableHead>Sport</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Total Compensation</TableHead>
                    <TableHead className="text-right"># Deals</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {athleteTaxData?.map((athlete) => (
                    <TableRow key={athlete.athlete_id}>
                      <TableCell className="font-medium">{athlete.athlete_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{athlete.athlete_sport}</Badge>
                      </TableCell>
                      <TableCell>{athlete.athlete_email || "No email"}</TableCell>
                      <TableCell className="text-right">
                        ${athlete.total_compensation.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">{athlete.deal_count}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => generate1099(athlete.athlete_id)}
                            disabled={generating === athlete.athlete_id}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {generating === athlete.athlete_id ? "Generating..." : "Generate"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card>
          <CardHeader>
            <CardTitle>1099-NEC Filing Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm">
              <strong>Who needs a 1099-NEC?</strong> Any athlete who received $600 or more in compensation during the tax year.
            </p>
            <p className="text-sm">
              <strong>Filing Deadline:</strong> January 31st of the following year (e.g., 2024 forms due January 31, 2025).
            </p>
            <p className="text-sm">
              <strong>What's included?</strong> All cash and non-cash compensation from NIL deals.
            </p>
            <p className="text-sm text-muted-foreground">
              Note: This tool generates forms for your records. Consult with a tax professional for official IRS filing.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
